import React, { useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import Loadable from 'react-loadable';
import AdminContainers from '../layout/AdminLayout';
import { getSubscriptionDetails } from '../utils/Utils';
import { getLocalStorage } from '../utils/BrowseStorage';

const Loading = () => <></>;

const MyLoadable = (component) =>
  Loadable({
    loader: component,
    loading: Loading,
  });


// Helper to wrap a component and inject extra props
const withExtraProps = (Component, extraProps) => (props) =>
  <Component {...props} {...extraProps} />;

const RouterList = () => {
  const [permission, setPermission] = useState('');
  const [isViewer, setIsViewer] = useState(false);


  useEffect(() => {
    if (permission === 'viewer') setIsViewer(true);
    else setIsViewer(false);
  }, [permission]);
  useEffect(() => {
    const storedColors = JSON.parse(sessionStorage.getItem('themeColors'));
    if (storedColors) {
      Object.keys(storedColors).forEach(colorKey => {
        document.documentElement.style.setProperty(`--${colorKey}`, storedColors[colorKey]);
      });
    }
    else {
      document.documentElement.style.setProperty(`--primary-color`, '#007aa8');
      document.documentElement.style.setProperty(`--secondary-color`, '#24aedf');
    }
  }, []);

  useEffect(() => {
    const value = getLocalStorage('permissionTypes');
    if (value?.role) setPermission(value.role);
  }, []);

  const subscriptionList = getSubscriptionDetails();

  const renderRoutes = () => {
    const routes = [];

    // Add administation routes
    routes.push(
      <Route
        exact
        path='/profile'
        component={withExtraProps(MyLoadable(() => import('../pages/settings/MyProfile')), { isViewer })}
      />
    );

    routes.push(
      <Route exact path='/account-settings'
        component={withExtraProps(MyLoadable(() => import('../pages/administation/AccountSettings')), { isViewer })}
      />
    );

    routes.push(
      <Route
        exact
        path='/organizations'
        component={withExtraProps(MyLoadable(() => import('../pages/settings/Organization/Organizations')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/users'
        component={withExtraProps(MyLoadable(() => import('../pages/settings/AllUsers')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/system-status'
        component={withExtraProps(MyLoadable(() => import('../pages/settings/SystemStatus')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/geo-fencing'
        component={withExtraProps(MyLoadable(() => import('../pages/settings/GeoFencing/GeoFencing')), { isViewer })}
      />
    );

    // Add Marketplace routes
    routes.push(
      <Route
        exact
        path='/market-place'
        component={withExtraProps(MyLoadable(() => import('../pages/Marketing/MarketPlace')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/subscription'
        component={withExtraProps(MyLoadable(() => import('../pages/Marketing/Subscription')), { isViewer })}
      />
    );

    // Add Accounts routes
    if (permission === 'owner') {
      routes.push(
        <Route
          exact
          path='/account-settings'
          component={withExtraProps(MyLoadable(() => import('../pages/administation/AccountSettings')), { isViewer })}
        />
      );
      routes.push(
        <Route
          exact
          path='/application-status'
          component={withExtraProps(MyLoadable(() => import('../pages/administation/ApplicationStatus')), { isViewer })}
        />
      );
      routes.push(
        <Route
          exact
          path='/manage-videos'
          component={withExtraProps(MyLoadable(() => import('../pages/administation/ManageVideos')), { isViewer })}
        />
      );
      routes.push(
        <Route
          exact
          path='/admin-dashboard'
          component={withExtraProps(MyLoadable(() => import('../pages/administation/AdminDashboard')), { isViewer })}
        />
      );
      routes.push(
        <Route
          exact
          path='/zoom-accounts'
          component={withExtraProps(MyLoadable(() => import('../pages/administation/Account')), { isViewer })}
        />
      );
      routes.push(
        <Route
          exact
          path='/zoom-logs'
          component={withExtraProps(MyLoadable(() => import('../pages/administation/ZoomLogs')), { isViewer })}
        />
      );
      routes.push(
        <Route
          exact
          path='/admin-document'
          component={withExtraProps(MyLoadable(() => import('../pages/HelpMenu')), { isViewer })}
        />
      );
    };

    routes.push(
      <Route
        exact
        path='/noc-dashboard'
        component={withExtraProps(MyLoadable(() => import('../pages/administation/NocDashboard/NocDashboardLayout')), { isViewer })}
      />
    );

    // Add default Application Routes
    routes.push(
      <Route
        exact
        path='/home'
        component={withExtraProps(MyLoadable(() => import('../pages/Home')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/privacy-terms'
        component={withExtraProps(MyLoadable(() => import('../pages/PrivacyandTerms')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/about-us'
        component={withExtraProps(MyLoadable(() => import('../pages/AboutUs')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/ticket'
        component={withExtraProps(MyLoadable(() => import('../pages/help/Ticket')), { isViewer })}
      />
    );
    routes.push(
      <Route
        exact
        path='/videos'
        component={withExtraProps(MyLoadable(() => import('../pages/help/ProductVideos')), { isViewer })}
      />
    );

    // Conditionally render CPE routes
    if (subscriptionList.includes('svb')) {
      routes.push(
        <React.Fragment key="cpe-routes">
          <Route
            exact
            path='/cpe-devices-dashboard'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/CPEDevicesDashboard')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-devices-onboarding'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/devices/CPEDeviceOnBoarding')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-list-devices'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/devices/CPEListDevices')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-device-details/:deviceId'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/devices/CPEDeviceDetails')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-device-update/:deviceId'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/devices/CPEDeviceOnBoarding')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-reports'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/devices/CPEReports')), { isViewer })}
          />
          <Route
            exact
            path='/connect-grid'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/devices/NetworkVisual/CPENetworkVisualization')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-supported-apps'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/images/AvailableImages')), { isViewer })}
          />
          <Route
            exact
            path='/cpe-deivice-apps'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/images/DeviceImages')), { isViewer })}
          />
          <Route
            exact
            path='/shell'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/administration/Shell')), { isViewer })}
          />
          <Route
            exact
            path='/device-logs'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/auditing/DeviceLogs')), { isViewer })}
          />
          <Route
            exact
            path='/app-reports'
            component={withExtraProps(MyLoadable(() => import('../CPEManageDashboard/auditing/AppReports')), { isViewer })}
          />
          <Route
            exact
            path='/dashboard-analytics'
            component={withExtraProps(MyLoadable(() => import('../analytics/dashboard-analytics')), { isViewer })}
          />
          <Route
            exact
            path='/app-analytics'
            component={withExtraProps(MyLoadable(() => import('../analytics/app-analytics')), { isViewer })}
          />
          <Route
            exact
            path='/user-analytics'
            component={withExtraProps(MyLoadable(() => import('../analytics/user-analytics')), { isViewer })}
          />
          <Route
            exact
            path='/site-analytics'
            component={withExtraProps(MyLoadable(() => import('../analytics/site-analytics')), { isViewer })}
          />
          <Route
            exact
            path='/user-details'
            component={withExtraProps(MyLoadable(() => import('../analytics/user-details')), { isViewer })}
          />
          <Route
            exact
            path='/dns-analytics'
            component={withExtraProps(MyLoadable(() => import('../analytics/dns-analytics')), { isViewer })}
          />
          <Route
            exact
            path='/svb-document'
            component={withExtraProps(MyLoadable(() => import('../pages/HelpMenu')), { isViewer })}
          />
        </React.Fragment>
      );
    }

    // Conditionally render SD-WAN routes
    if (subscriptionList.includes('sdwan')) {
      routes.push(
        <React.Fragment key="sdwan-routes">
          <Route
            exact
            path='/sdwan'
            component={withExtraProps(MyLoadable(() => import('../pages/SDWANDashboard')), { isViewer })}
          />
          <Route
            exact
            path='/access-token'
            component={withExtraProps(MyLoadable(() => import('../pages/settings/AccessTokens')), { isViewer })}
          />
          <Route
            exact
            path='/cne'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/Devices')), { isViewer })}
          />
          <Route
            exact
            path='/device-details/:deviceId'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/DeviceDetails')), { isViewer })}
          />
          <Route
            exact
            path='/virtual-links'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/Tunnels')), { isViewer })}
          />
          <Route
            exact
            path='/app-signatures'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/AppList')), { isViewer })}
          />
          <Route
            exact
            path='/app-signatures/add'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/AddEditApp')), { isViewer })}
          />
          <Route
            exact
            path='/app-signatures/:appIds'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/AddEditApp')), { isViewer })}
          />
          <Route
            exact
            path='/devices'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/Devices')), { isViewer })}
          />
          <Route
            exact
            path='/app-identify'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/AppList')), { isViewer })}
          />
          <Route
            exact
            path='/app-identify-details/:appIds?'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/AddEditApp')), { isViewer })}
          />
          <Route
            exact
            path='/device-token'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/DeviceToken')), { isViewer })}
          />
          <Route
            exact
            path='/jobs'
            component={withExtraProps(MyLoadable(() => import('../pages/history/Jobs')), { isViewer })}
          />
          <Route
            exact
            path='/notifications'
            component={withExtraProps(MyLoadable(() => import('../pages/history/Notifications')), { isViewer })}
          />
          <Route
            exact
            path='/routing-labels'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/PathTags')), { isViewer })}
          />
          <Route
            exact
            path='/path-tags'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/PathTags')), { isViewer })}
          />
          <Route
            exact
            path='/tunnel-graph'
            component={withExtraProps(MyLoadable(() => import('../pages/analytics/TunnelGraph')), { isViewer })}
          />
          <Route
            exact
            path='/device-location'
            component={withExtraProps(MyLoadable(() => import('../pages/analytics/DeviceLocation')), { isViewer })}
          />
          <Route
            exact
            path='/firewalls'
            component={withExtraProps(MyLoadable(() => import('../pages/security/Firewall')), { isViewer })}
          />
          <Route exact path='/firewall-details' component={MyLoadable(() => import('../pages/security/AddEditFirewall'))} />
          <Route
            exact
            path='/firewall-details/:firewallId'
            component={withExtraProps(MyLoadable(() => import('../pages/security/AddEditFirewall')), { isViewer })}
          />
          <Route
            exact
            path='/available'
            component={withExtraProps(MyLoadable(() => import('../pages/appstore/Available')), { isViewer })}
          />
          <Route
            exact
            path='/installed'
            component={withExtraProps(MyLoadable(() => import('../pages/appstore/Installed')), { isViewer })}
          />
          <Route
            exact
            path='/qos-policies'
            component={withExtraProps(MyLoadable(() => import('../pages/trafficOptimization/QosPolicy')), { isViewer })}
          />

          <Route exact path='/qos-policy-details' component={withExtraProps(MyLoadable(() => import('../pages/trafficOptimization/AddEditQosPolicy')))} />

          <Route
            exact
            path='/qos-policy-details/:policyId'
            component={withExtraProps(MyLoadable(() => import('../pages/trafficOptimization/AddEditQosPolicy')), { isViewer })}
          />
          <Route
            exact
            path='/peers'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/Peers')), { isViewer })}
          />
          <Route
            exact
            path='/peer-details/:peerId'
            component={withExtraProps(MyLoadable(() => import('../pages/connections/AddEditPeer')), { isViewer })}
          />
          <Route
            exact
            path='/path-selections'
            component={withExtraProps(MyLoadable(() => import('../pages/trafficOptimization/PathSelection')), { isViewer })}
          />
          <Route exact path='/policy-details' component={withExtraProps(MyLoadable(() => import('../pages/trafficOptimization/AddEditPolicy')))} />
          <Route
            exact
            path='/policy-details/:policyId'
            component={withExtraProps(MyLoadable(() => import('../pages/trafficOptimization/AddEditPolicy')), { isViewer })}
          />
          <Route
            exact
            path='/version'
            component={withExtraProps(MyLoadable(() => import('../pages/Versions')), { isViewer })}
          />
          <Route
            exact
            path='/doc'
            component={withExtraProps(MyLoadable(() => import('../pages/Document')), { isViewer })}
          />
          <Route
            exact
            path='/help'
            component={withExtraProps(MyLoadable(() => import('../pages/Help')), { isViewer })}
          />
          <Route
            exact
            path='/ip'
            component={withExtraProps(MyLoadable(() => import('../pages/ip/IpManagement')), { isViewer })}
          />
          <Route
            exact
            path='/network-traffic'
            component={withExtraProps(MyLoadable(() => import('../pages/NetworkTraffic')), { isViewer })}
          />
          <Route
            exact
            path='/quick-help'
            component={withExtraProps(MyLoadable(() => import('../pages/help/QuickHelp')), { isViewer })}
          />
          <Route
            exact
            path='/chat'
            component={withExtraProps(MyLoadable(() => import('../pages/help/Chat')), { isViewer })}
          />
          <Route
            exact
            path='/sdwan-reports'
            component={withExtraProps(MyLoadable(() => import('../pages/reports/Reports')), { isViewer })}
          />
          <Route
            exact
            path='/reports/firewall-history'
            component={withExtraProps(MyLoadable(() => import('../pages/reports/FirewallHistory')), { isViewer })}
          />
          <Route
            exact
            path='/network-dashboard'
            component={withExtraProps(MyLoadable(() => import('../pages/network-dashboard/NetworkDashboard')), { isViewer })}
          />
          <Route
            exact
            path='/reports/policy-status'
            component={withExtraProps(MyLoadable(() => import('../pages/reports/PolicyStatus')), { isViewer })}
          />
          <Route
            exact
            path='/sdwan-document'
            component={withExtraProps(MyLoadable(() => import('../pages/HelpMenu')), { isViewer })}
          />
        </React.Fragment>
      );
    }
    //Conditionally render kubernetes routes
    // if (subscriptionList.includes('KUBERNETES')) {
    routes.push(
      <React.Fragment key="kubernetes-routes">
       
        <Route
          exact
          path='/Nodes'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/cluster/cluster')), { isViewer })}
        />
         <Route
          exact
          path='/deployments'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/deployments/deployments')), { isViewer })}
        />
         <Route
          exact
          path='/deploymentsub/:deploymentId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/deployments/deploymentsub/collectiondata')), { isViewer })}
        />
        <Route
          exact
          path='/cronjobs'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/cornjobs/corn')), { isViewer })}
        />
         <Route
          exact
          path='/replicasets'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/replicaset.jsx/replica')), { isViewer })}
        />
         <Route
          exact
          path='/replicasetsub/:replicasetId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/replicaset.jsx/replicasubsets/collection')), { isViewer })}
        />
        <Route
          exact
          path='/demonsets'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/demonsets/demonsets')), { isViewer })}
        />
       <Route
          exact
          path='/subdemonsets/:demonsetId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/demonsets/demonsubsets/subdemonsets')), { isViewer })}
        />
         <Route
          exact
          path='/kubernetes-jobs'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/jobs/jobs')), { isViewer })}
        />
        
        <Route
          exact
          path='/jobs/:jobId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/jobs/jobssubmenu/collect')), { isViewer })}
        />
        <Route
          exact
          path='/Pods'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/pods/pods')), { isViewer })}
        />
         <Route
          exact
          path='/Pods/:podId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/pods/podSubmenu/collectionPods')), { isViewer })}
        />
        <Route
          exact
          path='/workload-overview'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/workloads/worklods overview/main')), { isViewer })}
        />
        <Route
          exact
          path='/services'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/service/serviceSection/servicemain')), { isViewer })}
        />
        <Route
          exact
          path='/services-dashboard/:serviceId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/service/serviceSection/subseviceSection/collection')), { isViewer })}
        />
         <Route
          exact
          path='/config-maps'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/config-stporage/configMap/configMap')), { isViewer })}
        />
         {/* <Route
          exact
          path='/storage-classes'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/config-stporage/configSubmenu.jsx/collection')), { isViewer })}
        /> */}
         <Route
          exact
          path='/ConfigMap/:storageClassesId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/config-stporage/configMap/configSubmenu/collection')), { isViewer })}
        />
         <Route
          exact
          path='/storage-classes'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/config-stporage/StorageClass/storageClassess')), { isViewer })}
        />
         <Route
          exact
          path='/StorageClasses/:storageClassesId'
          component={withExtraProps(MyLoadable(() => import('../pages/kubernate/config-stporage/StorageClass/StorageClassSubMenu/CollectionStorageClass')), { isViewer })}
        />





      </React.Fragment>
    );
    //  }


    // Conditionally render zoom routes   
    if (subscriptionList.includes('zoom')) {
      routes.push(
        <React.Fragment key="zoom-routes">
          <Route
            exact
            path='/zoom-dashboard'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/ZoomDashboard')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-users'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/UserManagement')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-meetings'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/MeetingManagement')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-webinars'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/WebinarManagement')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-reports'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/ZoomReports')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-recordings'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/ZoomRecordings')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-calender'
            component={withExtraProps(MyLoadable(() => import('../pages/Zoom/ZoomCalender')), { isViewer })}
          />
          <Route
            exact
            path='/zoom-document'
            component={withExtraProps(MyLoadable(() => import('../pages/HelpMenu')), { isViewer })}
          />
        </React.Fragment>
      );
    }

    return routes;
  };

  const Page404 = (props) => {
    return (
      <div style={{ padding: "1rem" }}>
        <h2 style={{ fontWeight: "bolder" }}>Error 404 </h2>
        <p>The page youâ€™re looking for was not found. </p>
      </div>
    )
  }

  return (
    <BrowserRouter>
      <Switch>
        <Route exact path='/' component={MyLoadable(() => import('../pages/authentication/SignInRegisteration'))} />
        <Route exact path='/login' component={MyLoadable(() => import('../pages/authentication/Login'))} />
        <Route exact path='/reset-password' component={MyLoadable(() => import('../pages/ResetPassword'))} />
        <Route exact path='/forgot-password' component={MyLoadable(() => import('../pages/ForgotPassword'))} />
        <Route exact path='/register-customer' component={MyLoadable(() => import('../pages/authentication/Register'))} />
        <Route exact path='/verify-email' component={MyLoadable(() => import('../pages/authentication/Otp'))} />
        <Route exact path='/test' component={MyLoadable(() => import('../components/TestComponent'))} />
        <Route exact path='/validate-email' component={MyLoadable(() => import('../pages/authentication/Email'))} />

        <Route
          render={({ history, location }) => {
            const isRouteMatched = (routes) => {
              for (let i = 0; i < routes.length; i++) {
                const route = routes[i];
                if (route.props?.path === location.pathname) {
                  return true;
                }
                if (route.props?.path && route?.props?.path?.includes(':')) {
                  const pathSegments = route.props.path.split('/');
                  const locationSegments = location.pathname.split('/');
                  if (pathSegments.length === locationSegments.length) {
                    let matched = true;
                    for (let j = 0; j < pathSegments.length; j++) {
                      if (pathSegments[j].startsWith(':')) continue;
                      if (pathSegments[j] !== locationSegments[j]) {
                        matched = false;
                        break;
                      }
                    }
                    if (matched) return true;
                  }
                }
                if (route.props?.children) {
                  if (isRouteMatched(React.Children.toArray(route.props.children))) {
                    return true;
                  }
                }
              }
              return false;
            };

            const isMatched = isRouteMatched(renderRoutes());

            console.log("Location:", location, isMatched, renderRoutes());

            return (
              <AdminContainers history={history} location={location} permission={permission}>
                {isMatched ? (
                  renderRoutes().filter((route) => {
                    if (
                      route.props.path === location.pathname ||
                      (route.props.children &&
                        isRouteMatched(React.Children.toArray(route.props.children)))
                    ) {
                      return true;
                    }
                    return false;
                  })
                ) : (
                  <Page404 />
                )}
              </AdminContainers>
            );
          }}
        />
      </Switch>
    </BrowserRouter>
  );
};

export default RouterList;


